import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// Invoice PDF renderer mirroring Android's InvoicePdfRenderer: A4, an INVOICE
// title, FROM + meta, BILL TO, a line-item table, right-aligned totals
// (subtotal / tax rate / tax / amount due), then optional BANK and NOTES blocks.

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// "d-MMM-yy" in UTC — matches Android's SimpleDateFormat("d-MMM-yy", UTC).
function fmtDate(millis) {
  if (!millis) return "-";
  const d = new Date(millis);
  return `${d.getUTCDate()}-${MONTHS_SHORT[d.getUTCMonth()]}-${String(d.getUTCFullYear()).slice(-2)}`;
}

function money(symbol, code, amount) {
  return `${symbol}${Number(amount).toFixed(2)} ${code}`;
}

function sanitize(name) {
  return (name || "client").replace(/[^A-Za-z0-9]+/g, "_").slice(0, 40) || "client";
}

/**
 * @param {Object} data
 * @param {Object} data.sender    {name,address,phone,bankSwift,bankName,bankAddress,accountNumber,beneficiaryName}
 * @param {Object} data.client    {name,address,taxId}
 * @param {string|number} data.invoiceNumber
 * @param {number} data.issueDate epoch ms
 * @param {number} data.dueDate   epoch ms
 * @param {Array}  data.lineItems [{description, quantityHours, unitPrice}]
 * @param {number} data.taxRatePercent
 * @param {string} data.notes
 * @param {Object} data.currency  {symbol, code}
 */
export function generateInvoicePdf(data) {
  const {
    sender = {},
    client = {},
    invoiceNumber = "",
    issueDate = 0,
    dueDate = 0,
    lineItems = [],
    taxRatePercent = 0,
    notes = "",
    currency = { symbol: "$", code: "USD" },
  } = data;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 40;
  const rightX = pageW - margin;
  let y = margin + 6;

  // ── Title ──
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("INVOICE", margin, y);

  // ── Meta (right column, aligned with the title row) ──
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  const metaLines = [
    `Invoice ID: ${invoiceNumber}`,
    `Issue date: ${fmtDate(issueDate)}`,
    `Due date: ${fmtDate(dueDate)}`,
  ];
  let metaY = margin + 2;
  metaLines.forEach((line) => {
    doc.text(line, rightX, metaY, { align: "right" });
    metaY += 14;
  });

  y += 26;

  // ── FROM ──
  const sectionLabel = (text, yy) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(102);
    doc.text(text, margin, yy);
    doc.setTextColor(20);
  };
  const bodyLine = (text, yy) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(text, margin, yy);
  };

  sectionLabel("FROM", y);
  y += 14;
  if (sender.name) { bodyLine(sender.name, y); y += 13; }
  if (sender.address) { bodyLine(sender.address, y); y += 13; }
  if (sender.phone) { bodyLine(`Tel: ${sender.phone}`, y); y += 13; }
  y += 8;

  // ── BILL TO ──
  sectionLabel("BILL TO", y);
  y += 14;
  bodyLine(client.name || "-", y); y += 13;
  if (client.address) { bodyLine(`Address: ${client.address}`, y); y += 13; }
  if (client.taxId) { bodyLine(`Tax ID: ${client.taxId}`, y); y += 13; }
  y += 10;

  // ── Line items table ──
  const subtotal = lineItems.reduce((s, it) => s + Number(it.quantityHours) * Number(it.unitPrice), 0);
  const taxAmount = subtotal * (Number(taxRatePercent) / 100);
  const amountDue = subtotal + taxAmount;

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [["DESCRIPTION", "QUANTITY", "UNIT PRICE", "AMOUNT"]],
    body: lineItems.map((it) => [
      it.description || "",
      Number(it.quantityHours).toFixed(2),
      money(currency.symbol, currency.code, it.unitPrice),
      money(currency.symbol, currency.code, Number(it.quantityHours) * Number(it.unitPrice)),
    ]),
    columnStyles: {
      0: { cellWidth: 235 },
      1: { cellWidth: 70, halign: "right" },
      2: { cellWidth: 100, halign: "right" },
      3: { cellWidth: 110, halign: "right" },
    },
    styles: { font: "helvetica", fontSize: 10, cellPadding: 5, overflow: "linebreak" },
    headStyles: { fontStyle: "bold", fillColor: [238, 238, 238], textColor: 30 },
  });

  // ── Totals (right-aligned under the table) ──
  let ty = doc.lastAutoTable.finalY + 18;
  const totalRow = (label, value, bold) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(bold ? 12 : 10);
    doc.text(label, rightX - 150, ty, { align: "left" });
    doc.text(value, rightX, ty, { align: "right" });
    ty += bold ? 18 : 15;
  };
  totalRow("SUBTOTAL", money(currency.symbol, currency.code, subtotal), false);
  totalRow("TAX RATE", `${Number(taxRatePercent)}%`, false);
  totalRow("TAX", money(currency.symbol, currency.code, taxAmount), false);
  totalRow("AMOUNT DUE", money(currency.symbol, currency.code, amountDue), true);

  // ── Bank information (only if any field present) ──
  const bankFields = [
    ["Bank SWIFT / Routing", sender.bankSwift],
    ["Bank Name", sender.bankName],
    ["Bank Address", sender.bankAddress],
    ["Account Number", sender.accountNumber],
    ["Beneficiary Name", sender.beneficiaryName],
  ].filter(([, v]) => v && String(v).trim());
  if (bankFields.length) {
    ty += 12;
    sectionLabel("BANK INFORMATION", ty);
    ty += 14;
    bankFields.forEach(([label, value]) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`${label}: ${value}`, margin, ty);
      ty += 13;
    });
  }

  // ── Notes (optional) ──
  if (notes && notes.trim()) {
    ty += 12;
    sectionLabel("NOTES", ty);
    ty += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    notes.split("\n").slice(0, 8).forEach((line) => {
      doc.text(line, margin, ty);
      ty += 13;
    });
  }

  doc.save(`invoice-${invoiceNumber || "draft"}-${sanitize(client.name)}.pdf`);
}
